import { alpha, Box, Stack, Theme } from '@mui/material';
import IconHandler from 'components/common/icon/IconHandler';
import React, { useEffect, useMemo, useRef } from 'react';
import { TopicsInfo } from 'api/editor/topics';
import TopicItem from './TopicItem';
import { useMentionItemLink, useMentionTermTooltip } from 'hooks/useMentionTermActions';
import MentionGlossaryTermTooltip from 'components/common/mention/MentionGlossaryTermTooltip';
import { customRequestAPI } from 'api/api';

export default function GlossaryPopupBody({
    topicsInfo,
    selectedTopic,
    showViewRelatedButton = true,
    viewRelatedButtonLabel,
    enableViewRelatedEvent = false,
    setSelectedTopic,
}: {
    topicsInfo: TopicsInfo;
    selectedTopic: number;
    showViewRelatedButton?: boolean;
    viewRelatedButtonLabel?: string;
    enableViewRelatedEvent?: boolean;
    setSelectedTopic: (topicId: number) => void;
}) {
    const glossaryTerms = useMemo(() => Object.keys(topicsInfo), [topicsInfo]).sort((a, b) => {
        const termA = topicsInfo[a].display_order;
        const termB = topicsInfo[b].display_order;

        return termA - termB;
    });
    const itemsRef = useRef<{ [key: string]: any }>({});

    const availableTermsIdsList = useMemo(() => {
        const result: number[] = [];
        Object.values(topicsInfo ?? {}).forEach((section: any) => {
            if (section && Array.isArray(section?.topics)) {
                section.topics.forEach((term: any) => {
                    if (term.topic_id) result.push(term.topic_id);
                });
            }
        });
        return result;
    }, [topicsInfo]);

    const bodyElementRef = useRef<any>(null);
    const {
        mentionGlossaryTermTooltipAnchor,
        mentionGlossaryTermTooltipInfo,
        hideMentionGlossaryTermTooltip,
        setMentionGlossaryTermTooltipContainer,
    } = useMentionTermTooltip();

    const onMentionItemLinkClick = (id: number, type: string, mentionItemData: any) => {
        if (type == 'glossary_term' && id && availableTermsIdsList.includes(id)) {
            setSelectedTopic(id);
        }
    };

    const { setMentionItemLinkData, setMentionItemLinkContainer } = useMentionItemLink({
        behaviorType: 'view',
        onMentionItemLinkClick: onMentionItemLinkClick,
        availableClickGlossaryTermsIds: availableTermsIdsList,
    });

    const updateMentionElementsData = () => {
        if (bodyElementRef.current) {
            const mentionList: any[] = [];
            const mentionElements = bodyElementRef.current.querySelectorAll('.mention-item');
            if (mentionElements.length > 0) {
                mentionElements.forEach((mentionElement: any) => {
                    mentionList.push({
                        type: mentionElement.getAttribute('data-type'),
                        id: mentionElement.getAttribute('data-id'),
                    });
                });
            }
            if (mentionList.length > 0) {
                customRequestAPI
                    .post(`/data/admin/glossary-setup/resolve`, {
                        items: mentionList,
                    })
                    .then((response: any) => {
                        if (response?.data?.items) {
                            const data = response.data.items.reduce((acc: any, item: any) => {
                                acc[`${item.type}_${item.id}`] = item;
                                return acc;
                            }, {});
                            setMentionItemLinkData(data);

                            Object.values(data).forEach((mentionItem: any) => {
                                const elementsToUpdate = bodyElementRef.current.querySelectorAll(
                                    `.mention-item[data-type="${mentionItem.type}"][data-id="${mentionItem.id}"] span`,
                                );
                                if (elementsToUpdate) {
                                    elementsToUpdate.forEach((updateItem: any) => {
                                        updateItem.textContent = mentionItem.name;
                                    });
                                }
                            });
                        }
                    })
                    .catch((error: any) => {});
            }
        }
    };
    useEffect(() => {
        setMentionItemLinkContainer(bodyElementRef.current);
        setMentionGlossaryTermTooltipContainer(bodyElementRef.current);
        updateMentionElementsData();
    }, []);

    useEffect(() => {
        setTimeout(() => {
            itemsRef.current[selectedTopic]?.scrollIntoView({ behavior: 'smooth' });
        }, 200);
    }, [selectedTopic]);

    return (
        <Stack
            direction={'column'}
            key={'glossary-terms-body'}
            sx={{ height: '672px', flexGrow: 1, overflow: 'auto', marginTop: '0 !important' }}
            ref={bodyElementRef}
        >
            {glossaryTerms.map((term) => {
                const topicInfo = topicsInfo[term];

                const icon =
                    topicInfo.use_icon === 'fa' ? (
                        <Stack
                            flexShrink={0}
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            width={16}
                            height={16}
                            sx={{ mt: '2px', fontSize: 14 }}
                        >
                            <IconHandler icon={{ type: 'fa', value: topicInfo.fa_icon }} />
                        </Stack>
                    ) : topicInfo.use_icon === 'uploaded' && topicInfo.icon ? (
                        <Stack
                            flexShrink={0}
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            width={16}
                            height={16}
                            sx={{ mt: '2px' }}
                        >
                            <Box
                                component={'img'}
                                src={`data:${topicInfo.icon_type};base64, ${topicInfo.icon}`}
                                alt={`tag-icon-${term}`}
                                width={14}
                                height={14}
                            />
                        </Stack>
                    ) : null;

                return topicInfo.topics.map((topic) => (
                    <Box
                        ref={(el) => (itemsRef.current[topic.topic_id] = el)}
                        key={`glossary-item-${topic.topic_id}`}
                        sx={{
                            position: 'relative',
                            p: 2,
                            borderTop: (theme: Theme) =>
                                topic.topic_id == selectedTopic
                                    ? `2px solid ${theme.palette.primary.main}`
                                    : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                        }}
                    >
                        <Box sx={{ overflow: 'hidden' }}>
                            <TopicItem
                                topicInfo={topic}
                                sectionIcon={icon}
                                showViewRelatedButton={showViewRelatedButton}
                                viewRelatedButtonLabel={viewRelatedButtonLabel}
                                enableViewRelatedEvent={enableViewRelatedEvent}
                            />
                        </Box>
                    </Box>
                ));
            })}
            <MentionGlossaryTermTooltip
                data={mentionGlossaryTermTooltipInfo}
                anchor={mentionGlossaryTermTooltipAnchor}
                onLeave={hideMentionGlossaryTermTooltip}
            />
        </Stack>
    );
}
